import {
  getSectionSubTaskIds,
  pairDuplicatedSectionTasks,
  updateLinkedLifeArea,
  updateLinkedRelationships,
} from '@griply/services';
import { useCallback } from 'react';
import { useDuplicateTasksMutation } from 'shared/hooks/queries/use-duplicate-tasks-mutation';
import { useRelationships } from 'shared/hooks/use-relationships';
import {
  getLifeAreaDocById,
  updateLifeArea,
  updateRelationshipReferences,
} from 'shared/services/backend-api';
import { getTaskDocs } from 'shared/services/backend-api/get-tasks';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { Task } from 'shared/types/task';
import { chunkArray } from 'shared/utils/chunk-array';
import { v4 as uuidv4 } from 'uuid';

import { useUpdateLifeArea } from './use-update-life-area';

export const useDuplicateLifeAreaSection = () => {
  const { mutateAsync: duplicateTasks } = useDuplicateTasksMutation();
  const { submit: applyLifeAreaUpdate } = useUpdateLifeArea();
  const relationships = useRelationships();

  return useCallback(
    async (sectionId: ID, lifeArea: LifeArea) => {
      // get the section out of the life area
      const sectionIndex =
        lifeArea.sections?.findIndex((section) => section.id === sectionId) ??
        -1;

      const section = lifeArea.sections?.[sectionIndex];
      if (!section) {
        return;
      }

      // get tasks of life area
      const mainTasks = await getTaskDocs({ lifeAreas: [lifeArea.id] });

      // filter tasks to only those in the section
      const sectionTasks = section.tasks
        .map((taskId) => mainTasks.find(({ id }) => id === taskId))
        .filter(Boolean) as Task[];

      // get subtasks of all sectionTasks
      const subTaskIds = getSectionSubTaskIds(sectionTasks);
      const subTasks = await Promise.all(
        chunkArray(subTaskIds, 30).map(
          async (chunkedSubTaskIds) =>
            await getTaskDocs({ ids: chunkedSubTaskIds }),
        ),
      );

      // duplicate tasks
      const oldTasks = [...sectionTasks, ...subTasks.flat(1)];
      const newTasks = await duplicateTasks(oldTasks);

      const { mainTaskPairs, newSection } = pairDuplicatedSectionTasks({
        section,
        sectionTasks,
        oldTasks,
        newTasks,
        newSectionId: uuidv4(),
      });

      // Tags and life-area task order live in separate relationship / life-area
      // documents keyed by task id, not on the task doc — copying the task docs
      // alone drops them, so re-point those references at the new tasks.
      const linkedUpdates = mainTaskPairs.length
        ? [
            updateLinkedRelationships({
              taskPairs: mainTaskPairs,
              relationships,
              updateRelationshipReferences,
            }),
            updateLinkedLifeArea({
              taskPairs: mainTaskPairs,
              getLifeAreaDocById,
              updateLifeArea,
            }),
          ]
        : [];

      // add section to life area below selected section
      const newSections = lifeArea.sections?.toSpliced(
        sectionIndex + 1,
        0,
        newSection,
      );
      // update the life area
      applyLifeAreaUpdate({
        id: lifeArea.id,
        sections: newSections,
      });

      await Promise.all(linkedUpdates);
    },
    [duplicateTasks, relationships, applyLifeAreaUpdate],
  );
};
